import React, { useEffect, useMemo, useRef, useState } from "react";
import { EXECUTIVE_PILLARS, EXECUTIVE_SIZES, EXECUTIVE_STATUSES, getExecutiveItems, isExecutiveItemVisible } from "./executiveRoadmap";
import "./ExecutiveRoadmapView.css";

const VIEWS = [{ id: "plan", label: "Planned & In Flight" }, { id: "done", label: "Completed & Shipping Soon" }];

function StatusSelect({ item, onChange, inDialog = false }) {
  const palette = EXECUTIVE_STATUSES[item.status];
  const style = { color: palette.color, backgroundColor: palette.background };
  return onChange ? <select
    className={inDialog ? "executive-field" : "executive-status"}
    aria-label={`Status for ${item.id}`}
    value={item.status} style={style}
    onPointerDown={e => e.stopPropagation()}
    onClick={e => e.stopPropagation()}
    onChange={e => onChange(e.target.value)}
  >{Object.keys(EXECUTIVE_STATUSES).map(status => <option key={status}>{status}</option>)}</select>
    : <span className="executive-status" style={style}>{item.status}</span>;
}

function SizeSelect({ item, onChange, inDialog = false }) {
  const size = EXECUTIVE_SIZES.find(option => option.id === item.size);
  const title = size ? `${size.label} (${size.description})` : "Not sized";
  return onChange ? <select
    className={inDialog ? "executive-field" : "executive-size"}
    aria-label={`Size for ${item.id}`} title={title}
    value={item.size}
    onPointerDown={e => e.stopPropagation()}
    onClick={e => e.stopPropagation()}
    onChange={e => onChange(e.target.value)}
  >
    <option value="">{inDialog ? "Not sized" : "—"}</option>
    {EXECUTIVE_SIZES.map(option => <option key={option.id} value={option.id}>
      {inDialog ? `${option.label} (${option.description})` : option.label}
    </option>)}
  </select> : <span className={inDialog ? "executive-field" : "executive-size"}
    aria-label={`Size for ${item.id}`} title={title}>{size ? (inDialog ? title : size.label) : (inDialog ? "Not sized" : "—")}</span>;
}

function InitiativeDialog({ item, items, project, onUpdate, onEdit, onClose }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  const column = items.filter(i => i.pillar === item.pillar);
  const position = column.findIndex(i => i.id === item.id);
  const moveToPosition = index => {
    const others = column.filter(i => i.id !== item.id);
    onUpdate({ type: "move", id: item.id, pillar: item.pillar,
      ...(others[index] ? { beforeId: others[index].id } : others.length ? { afterId: others[others.length - 1].id } : {}) });
  };
  return <dialog ref={dialogRef} className="executive-dialog" aria-labelledby="executive-dialog-title"
    onCancel={onClose} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="executive-dialog-content">
      <div className="executive-dialog-top"><span>{item.id}</span><button type="button" onClick={onClose} aria-label="Close initiative">×</button></div>
      <h2 id="executive-dialog-title">{item.name}</h2>
      <label className="executive-label">Executive status
        <StatusSelect item={item} inDialog onChange={onUpdate ? status => onUpdate({ type: "status", id: item.id, status }) : undefined} />
      </label>
      <label className="executive-label">Size
        <SizeSelect item={item} inDialog onChange={onUpdate ? size => onUpdate({ type: "size", id: item.id, size }) : undefined} />
      </label>
      <label className="executive-label">Column
        <select className="executive-field" aria-label={`Column for ${item.id}`} value={item.pillar} disabled={!onUpdate}
          onChange={e => onUpdate({ type: "move", id: item.id, pillar: e.target.value })}>
          {EXECUTIVE_PILLARS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="executive-label">Priority in this list
        <select className="executive-field" aria-label={`Priority for ${item.id}`} value={position} disabled={!onUpdate}
          onChange={e => moveToPosition(Number(e.target.value))}>
          {column.map((_, index) => <option key={index} value={index}>{index + 1}{index === 0 ? " — Highest priority" : index === column.length - 1 ? " — Lowest priority" : ""}</option>)}
        </select>
      </label>
      {onUpdate && <p className="executive-dialog-hint">Changes save automatically. Executive status is separate from the sprint schedule.</p>}
      <div className="executive-dialog-actions">
        {project && onEdit && <button type="button" className="executive-secondary" onClick={() => { onClose(); onEdit(project); }}>Open project details</button>}
        <button type="button" className="executive-primary" onClick={onClose}>Done</button>
      </div>
    </div>
  </dialog>;
}

export default function ExecutiveRoadmapView({ projects = [], backlog = [], completed = [], board, onUpdate, onEdit }) {
  const [view, setView] = useState("plan");
  const [editingId, setEditingId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const dragRef = useRef(null);
  const suppressClick = useRef(false);
  const allItems = useMemo(() => getExecutiveItems(board), [board]);
  const items = allItems.filter(item => isExecutiveItemVisible(item, view));
  const lookup = useMemo(() => {
    const map = new Map();
    [...projects, ...backlog, ...completed].forEach(p => { if (p.keyCode) map.set(p.keyCode.trim().toUpperCase(), p); });
    return map;
  }, [projects, backlog, completed]);
  const update = onUpdate ? action => {
    onUpdate(action);
    setAnnouncement(action.type === "status" ? `${action.id}: ${action.status}`
      : action.type === "size" ? `${action.id} size: ${action.size || "Not sized"}`
        : `${action.id} priority updated in ${EXECUTIVE_PILLARS.find(p => p.id === action.pillar)?.name}`);
  } : undefined;
  const finishDrag = () => { dragRef.current = null; setDraggingId(null); setDropTarget(null); };
  const targetForCard = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { pillar: item.pillar, ...(event.clientY < rect.top + rect.height / 2 ? { beforeId: item.id } : { afterId: item.id }) };
  };
  const allowDrop = (event, target) => {
    if (!onUpdate || !dragRef.current) return;
    event.preventDefault(); event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    setDropTarget(target);
  };
  const drop = (event, target) => {
    if (!onUpdate || !dragRef.current) return;
    event.preventDefault(); event.stopPropagation();
    update({ type: "move", id: dragRef.current, ...target });
    finishDrag();
  };
  const editing = allItems.find(item => item.id === editingId);
  // Keep the dialog open when its status change removes the card from this tab.
  const dialogItems = editing && !items.some(i => i.id === editing.id) ? allItems : items;
  return <div className="executive-roadmap">
    <div className="executive-toolbar">
      <div><div className="executive-eyebrow">GasBuddy · H2 FY26 Roadmap</div>
        <h1>{view === "plan" ? `${items.length} initiatives across 5 strategic pillars` : `${items.filter(i => i.status !== "Deprioritized").length} initiatives completed or shipping soon${items.some(i => i.status === "Deprioritized") ? ` · ${items.filter(i => i.status === "Deprioritized").length} deprioritized` : ""}`}</h1>
      </div>
      <div className="executive-tabs" aria-label="Executive roadmap view">
        {VIEWS.map(v => <button type="button" key={v.id} aria-pressed={view === v.id} onClick={() => { setView(v.id); finishDrag(); }}>{v.label}</button>)}
      </div>
    </div>
    <p className="executive-help">{onUpdate ? "Drag cards between columns or up and down to set priority. Click a status or size to change it." : "Initiatives are listed in priority order within each column."}</p>
    <div className="executive-board" aria-label="Strategic pillars">
      {EXECUTIVE_PILLARS.map(pillar => {
        const column = items.filter(i => i.pillar === pillar.id);
        const deprioritized = column.filter(i => i.status === "Deprioritized").length;
        return <section key={pillar.id} className={`executive-column${dropTarget?.pillar === pillar.id ? " executive-column-target" : ""}`}
          style={{ "--pillar-color": pillar.color }}
          aria-labelledby={`executive-pillar-${pillar.id}`}
          onDragOver={e => allowDrop(e, { pillar: pillar.id })} onDrop={e => drop(e, { pillar: pillar.id })}>
          <header className="executive-column-header"><h2 id={`executive-pillar-${pillar.id}`}>{pillar.name}</h2>
            <p>{column.length} {column.length === 1 ? "initiative" : "initiatives"}{deprioritized > 0 ? ` · ${deprioritized} deprioritized` : ""}</p>
          </header>
          <div className="executive-card-list" role="list" aria-label={`${pillar.name} initiatives`}>
            {column.map((item, index) => <article key={item.id} role="listitem" data-initiative={item.id}
              className={`executive-card${draggingId === item.id ? " executive-card-dragging" : ""}${dropTarget?.beforeId === item.id ? " executive-drop-before" : ""}${dropTarget?.afterId === item.id ? " executive-drop-after" : ""}`}
              draggable={!!onUpdate}
              onDragStart={e => {
                if (!onUpdate || e.target.closest("select")) { e.preventDefault(); return; }
                suppressClick.current = true;
                dragRef.current = item.id; setDraggingId(item.id);
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", item.id);
              }}
              onDragEnd={finishDrag}
              onDragOver={e => allowDrop(e, targetForCard(e, item))}
              onDrop={e => drop(e, targetForCard(e, item))}>
              <div className="executive-card-top"><span className="executive-key">{onUpdate && <span className="executive-grip" aria-hidden="true">⠿</span>}{item.id}</span>
                <StatusSelect item={item} onChange={update ? status => update({ type: "status", id: item.id, status }) : undefined} />
              </div>
              <button type="button" className="executive-card-title" aria-label={`Open ${item.id}: ${item.name}`}
                onPointerDown={() => { suppressClick.current = false; }}
                onClick={e => { if (!suppressClick.current || e.detail === 0) setEditingId(item.id); suppressClick.current = false; }}>{item.name}</button>
              <div className="executive-card-footer">
                <label className="executive-card-size">SIZE
                  <SizeSelect item={item} onChange={update ? size => update({ type: "size", id: item.id, size }) : undefined} />
                </label>
                <span className="executive-priority" title={`Priority ${index + 1}`}>{index + 1}</span>
              </div>
            </article>)}
            {column.length === 0 && <div className="executive-empty">{onUpdate ? "Drop an initiative here" : "No initiatives"}</div>}
            {draggingId && <div className={`executive-drop-end${dropTarget?.pillar === pillar.id && !dropTarget.beforeId && !dropTarget.afterId ? " executive-drop-end-active" : ""}`}>Move to end of list</div>}
          </div>
        </section>;
      })}
    </div>
    <section className="executive-size-legend" aria-labelledby="executive-size-legend-title">
      <h2 id="executive-size-legend-title">T-shirt size legend</h2>
      <dl>{EXECUTIVE_SIZES.map(size => <div key={size.id}>
        <dt>{size.id === "XS" ? "1 = XS" : size.label}</dt>
        <dd>{size.description}</dd>
      </div>)}</dl>
    </section>
    <div className="executive-sr-only" role="status" aria-live="polite">{announcement}</div>
    {editing && <InitiativeDialog key={editing.id} item={editing} items={dialogItems} project={lookup.get(editing.id.toUpperCase())}
      onUpdate={update} onEdit={onEdit} onClose={() => setEditingId(null)} />}
  </div>;
}
